import { useQuery } from '@apollo/client/react';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import loadable from 'sku/@loadable/component';

import { gql } from 'src/__generated__';
import type {
  GetApplicationQuestionsQuery,
  GetApplicationQuestionsQueryVariables,
  Resume,
  SubmitApplicationInput,
} from 'src/__generated__/graphql';
import { ROLE_REQUIREMENT_NAMESPACE } from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import { StepId, useStepsCompleted } from 'src/context/use-steps-completed';
import { useJobId } from 'src/context/useJobId';
import { RootFormProvider } from 'src/forms/RootFormProvider';
import { useMatch } from 'src/hooks/useMatch';
import { Documents } from 'src/pages/Documents/Documents';
import { useDocuments } from 'src/pages/Documents/hooks/useDocuments';
import { Profile } from 'src/pages/Profile/Profile';
import ReviewPage from 'src/pages/Review/ReviewPage';
import { RoleRequirements } from 'src/pages/RoleRequirements/RoleRequirements';
import PageLoader from 'src/ui/PageLoader/PageLoader';
import { Progress } from 'src/ui/Progress/Progress';

import translations from './.vocab';
import { ApplyLayout } from './components/ApplyLayout';
import { Protected } from './components/Protected';

const Success = loadable(
  () => import(/* webpackPrefetch: true */ 'src/pages/Success/Success'),
);

const LinkOutSuccess = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/pages/LinkOutSuccess/LinkOutSuccess'
    ),
);

export const APPLY_PAGE_QUERY = gql(`
  query GetApplicationQuestions($jobId: ID!) {
    viewer {
      _id
    }
    jobApplicationProcess(jobId: $jobId) {
      questionnaire {
        questions {
          id
          __typename
        }
      }
      job {
        id
        title
        advertiser {
          id
          name
        }
      }
    }
  }
`);

let timeout: ReturnType<typeof setTimeout> | undefined;

const initialState: Partial<SubmitApplicationInput> = {
  resume: undefined,
  coverLetter: undefined,
  selectionCriteria: undefined,
};

type ApplyPageProps = {
  isLinkOutSuccess?: boolean;
};

export const ApplyPage = ({ isLinkOutSuccess }: ApplyPageProps) => {
  const titleRef = useRef<HTMLElement | null>(null);
  const { jobId } = useJobId();
  const { t } = useTranslations(translations);
  const navigate = useNavigate();
  const location = useLocation();
  const jobRootPath = useMelwaysLink(`/job/${jobId}/apply`);

  const [formPayload] = useState(initialState);
  const { data } = useQuery<
    GetApplicationQuestionsQuery,
    GetApplicationQuestionsQueryVariables
  >(APPLY_PAGE_QUERY, {
    variables: { jobId },
    returnPartialData: true,
  });
  const [isNewToWorkforce, setIsNewToWorkforce] = useState(false);

  const job = data?.jobApplicationProcess?.job || null;
  const jobTitle = job?.title || null;
  const company = job?.advertiser?.name || null;
  const questions = data?.jobApplicationProcess?.questionnaire?.questions || [];
  const shouldRenderRoleRequirements =
    questions.filter(
      (question) => question.__typename !== 'PrivacyPolicyQuestion',
    ).length > 0;

  const { completed, setCompleted } = useStepsCompleted();
  const match = useMatch('/:lang?/job/:id/apply/:step');
  const totalSteps = shouldRenderRoleRequirements
    ? ['', 'role-requirements', 'profile', 'review']
    : ['', 'profile', 'review'];
  const currentStepIndex = totalSteps.indexOf(match?.params.step || '');
  const isAllStepsComplete = totalSteps.length === completed.length + 1;

  useEffect(() => {
    const clear = () => timeout && clearTimeout(timeout);
    const scroll = () =>
      titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (match?.params.step !== 'success' && titleRef.current !== null) {
      clear();
      // role-requirements page doesn't scroll for some reason
      // this fixes it
      timeout = setTimeout(scroll, 10);
    }

    return clear;
  }, [match?.params.step]);

  const handleNavigate = (step?: string) => {
    if (step !== undefined) {
      // piping through search params is for cypress tests to work
      // since it rely on it to force a submit error
      const targetPath = step === '' ? jobRootPath : `${jobRootPath}/${step}`;

      navigate(
        { pathname: targetPath, search: location.search },
        { replace: true },
      );
    }
  };
  const handleNext = () => handleNavigate(totalSteps[currentStepIndex + 1]);
  const handleBack = () => handleNavigate(totalSteps[currentStepIndex - 1]);

  const {
    resumes,
    defaultResume,
    defaultCoverletter,
    shouldRenderselectionCriteria,
    loading: documentsLoading,
    error: documentsQueryError,
  } = useDocuments();

  return (
    <RootFormProvider defaultValues={{ [ROLE_REQUIREMENT_NAMESPACE]: {} }}>
      <Routes>
        <Route
          element={
            <ApplyLayout
              titleRef={titleRef}
              header={
                <Box data={{ automation: 'job-header' }}>
                  <Progress
                    current={currentStepIndex}
                    progress={completed.length}
                    shouldIncludeRoleRequirements={shouldRenderRoleRequirements}
                  />
                </Box>
              }
            />
          }
        >
          <Route
            path="/"
            element={
              documentsLoading ? (
                <PageLoader />
              ) : (
                <Documents
                  errorMessage={documentsQueryError && t('Server error')}
                  resumes={resumes as Resume[]}
                  defaultResume={defaultResume as Resume}
                  defaultCoverletter={defaultCoverletter}
                  shouldRenderselectionCriteria={Boolean(
                    shouldRenderselectionCriteria,
                  )}
                  onNext={() => {
                    setCompleted(StepId.DOCUMENTS_ID);
                    handleNext();
                  }}
                />
              )
            }
          />
          <Route element={<Protected />}>
            {shouldRenderRoleRequirements && (
              <Route
                path="role-requirements"
                element={
                  <RoleRequirements onPrev={handleBack} onNext={handleNext} />
                }
              />
            )}
            <Route
              path="profile"
              element={
                <Profile
                  isNewToWorkforce={isNewToWorkforce}
                  onNewToWorkforceToggle={setIsNewToWorkforce}
                  onPrev={handleBack}
                  onNext={handleNext}
                />
              }
            />
            <Route
              path="review"
              element={
                <ReviewPage
                  isNewToWorkforce={isNewToWorkforce}
                  onNewToWorkforceToggle={setIsNewToWorkforce}
                  formPayload={formPayload}
                />
              }
            />
            <Route path="*" element={<Navigate to="." replace />} />
          </Route>
        </Route>

        {isLinkOutSuccess && (
          <Route path="external" element={<LinkOutSuccess />} />
        )}

        {isAllStepsComplete && (
          <Route
            path="success"
            element={
              <Success
                fallback={<PageLoader />}
                jobTitle={jobTitle}
                company={company}
              />
            }
          />
        )}
      </Routes>
    </RootFormProvider>
  );
};
